import './index.css'

import cn from 'clsx'
import * as React from 'react'
import { connect } from 'react-redux'

import { CUSTOM_INBOXES } from '@disco/constants/FeatureTypes'
// import ExplorerActionMenu from 'components/explorer/action-menu'
import { CUSTOM_INBOX } from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import * as AppActions from '@disco/state-management/actions/AppActions'
import * as ExplorerActions from '@disco/state-management/actions/ExplorerActions'
import {
  uploadFilesToBrowse,
  uploadFilesToGroups,
} from '@disco/state-management/actions/upload-actions'
import * as UserActions from '@disco/state-management/actions/UserActions'
import type { State as ExplorerState } from '@disco/state-management/reducers/explorer'
import {
  getPlaylistCreatorMaxWidth,
  isDeviceTypePhone,
} from '@disco/state-management/selectors/app'
import {
  getAutoTaggingTracksSent,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import { getExplorerColumnSetting } from '@disco/state-management/selectors/user'
import {
  getConstrainedColCount,
  getExplorerLeftOffset,
  getExplorerPaneWidth,
  getExplorerWidth,
} from '@disco/utils/elementSizes'
// TODO: fully delete `getSearchHeight`
// import { getSearchHeight } from '@disco/state-management/selectors/search'
import { isTargetEditable, keyEqualTo } from '@disco/utils/keyboard'

import type { ExplorerPane } from 'types/explorer'

import AutoTagging from 'components/explorer/auto-tagging'
import Alert from 'components/ui/alert'
import ContactUsLink from 'components/ui/contact-us-link'

import ExplorerDropWrapper from 'containers/ExplorerDropWrapper'

import FacetedRow from './faceted-row'
import PaneList from './pane-list'

const mapStateToProps = state => {
  const isFeatureEnabled = makeIsFeatureEnabled()
  return {
    explorer: state.explorer,
    search: state.search,
    groups: state.groups,
    app: state.app,
    isPhone: isDeviceTypePhone(state),
    user: state.user,
    customInboxesEnabled: isFeatureEnabled(state, {
      featureName: CUSTOM_INBOXES,
    }),
    autoTagging: getAutoTaggingTracksSent(state),
    columnSettings: getExplorerColumnSetting(state),
    playlistCreatorWidth: getPlaylistCreatorMaxWidth(state),
  }
}

const mapDispatchToProps = {
  ...ExplorerActions,
  ...AppActions,
  ...UserActions,
  uploadFilesToBrowse,
  uploadFilesToGroups,
  sendMixpanelEvent,
}
type Props = {
  moveActivePane: (...args: Array<any>) => any
  handleResize: (...args: Array<any>) => any
  loadLibrary: (...args: Array<any>) => any
  toggleSidebar: (...args: Array<any>) => any
  openSidebar: (...args: Array<any>) => any
  user: Record<string, any>
  explorer: ExplorerState
  isPhone: boolean
  app: Record<string, any>
  search: Record<string, any>
  // reducer
  groups: Record<string, any>
  sendMixpanelEvent: (...args: Array<any>) => any
  uploadFilesToBrowse: typeof uploadFilesToBrowse
  uploadFilesToGroups: typeof uploadFilesToGroups
  customInboxesEnabled: boolean
  autoTagging: {
    auto_tagging_tracks_sent: number
  }
  columnSettings: Record<string, any>
  playlistCreatorWidth: number
}

function calcExplorerOffset(props) {
  const { columnSettings, app, playlistCreatorWidth } = props
  // TODO: switch from launchedOnMobileDevice -> isPhone
  const { windowWidth, sidebarOpen, shortlistsOpen, launchedOnMobileDevice } =
    app

  const explorerWidth = getExplorerWidth(
    windowWidth,
    sidebarOpen,
    shortlistsOpen,
    launchedOnMobileDevice,
    playlistCreatorWidth,
  )
  // use the explorerWidth calculated above to keep in sync for calculating Pane width
  const colWidth = getExplorerPaneWidth(
    windowWidth,
    columnSettings,
    sidebarOpen,
    shortlistsOpen,
    launchedOnMobileDevice,
    explorerWidth,
    playlistCreatorWidth,
  )
  const colCount = getConstrainedColCount(explorerWidth, columnSettings)
  const { activePane } = props.explorer
  const colMargin = 10

  // Calculate positioning for mobile devices
  const leftOffset = getExplorerLeftOffset(windowWidth, launchedOnMobileDevice)
  let innerOffset =
    activePane > colCount - 1
      ? (colWidth + colMargin) * (activePane - (colCount - 1))
      : 0
  return {
    innerOffset,
    colWidth,
    colCount,
    explorerWidth,
    leftOffset,
  }
}

class Explorer extends React.Component<
  Props,
  {
    firstPaneVisible: boolean
  }
> {
  state = {
    firstPaneVisible: false,
  }
  _paneNodeRef = React.createRef<HTMLDivElement>()
  _handleKeyDown = (e: KeyboardEvent) => {
    const modifiersPressed = e.altKey || e.shiftKey || e.metaKey || e.ctrlKey
    const modalOpened = window.location.pathname !== '/'
    if (isTargetEditable(e) || modifiersPressed || modalOpened) return

    if (keyEqualTo(e, 'ArrowLeft')) {
      e.preventDefault()
      return this.props.moveActivePane(-1)
    }

    if (keyEqualTo(e, 'ArrowRight')) {
      e.preventDefault()
      return this.props.moveActivePane(1)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.props.handleResize)
    document.removeEventListener('keydown', this._handleKeyDown)
  }

  componentDidMount() {
    window.addEventListener('resize', this.props.handleResize)
    document.addEventListener('keydown', this._handleKeyDown)
  }

  componentDidUpdate(prevProps) {
    const currOffset = calcExplorerOffset(this.props).innerOffset
    const prevOffset = calcExplorerOffset(prevProps).innerOffset

    if (currOffset !== prevOffset) {
      this._scrollActivePaneToOffset(currOffset)
    }
  }

  _scrollActivePaneToOffset(offset) {
    if (!this._paneNodeRef.current) return

    this._paneNodeRef.current.scrollTo({
      left: offset,
      top: 0,
      behavior: 'smooth',
    })
  }

  // _isInbox() {
  //   if (this.props.explorer.panes.length === 0) {
  //     return false
  //   }
  //   const groupType = this.props.explorer.panes[0].groupType
  //   return INBOX_TYPES.includes(groupType)
  // }
  render() {
    const { sidebarOpen } = this.props.app
    const { panes, uploads } = this.props.explorer
    const { colWidth, colCount, explorerWidth, leftOffset } =
      calcExplorerOffset(this.props)
    const innerStyles: React.CSSProperties = {
      width: colWidth * colCount,
      transform: undefined,
    }

    if (this.props.isPhone) {
      const { activePane } = this.props.explorer
      innerStyles['transform'] = `translate3d(${activePane * -100}%, 0, 0)`
    }

    const {
      customInboxesEnabled,
      autoTagging: { auto_tagging_tracks_sent },
    } = this.props
    // TODO: clean-up title/copyButton setup
    // let title = 'My Catalogs'
    // if (panes.length) {
    //   title =
    //     panes[0].internal_name && panes[0].internal_name.length > 0
    //       ? panes[0].internal_name
    //       : panes[0].name
    // }
    // let copyButton = null
    // if (this._isInbox() && panes.length) {
    //   let url = panes[0].uploadUrl
    //   if (!url) {
    //     const group = this.props.groups.groups.find(
    //       group => group.id === panes[0].groupId,
    //     )
    //     if (group) url = group.upload_url
    //   }
    //   copyButton = url ? (
    //     <CopyInboxUrl
    //       user={this.props.user}
    //       url={url}
    //       sendMixpanelEvent={this.props.sendMixpanelEvent}
    //     />
    //   ) : null
    // }
    const style = {
      width: explorerWidth,
      ...(leftOffset > 0 && { left: leftOffset }),
    }
    const firstPane: ExplorerPane | null | undefined = panes[0] as ExplorerPane
    const autoTagging = this.props.groups.groups.find(l => l.is_auto_tagged)
    const isAutoTagging = autoTagging && autoTagging.id === firstPane?.groupId
    const sidebarCanSlide = !this.props.app.launchedOnMobileDevice
    const explorerClassName = cn('Explorer', {
      'sidebar-open': sidebarOpen && sidebarCanSlide,
      'faceted-search-open': this.props.search.searchedNativeTags.length > 0, // TODO: move upper
    })

    if (
      isAutoTagging &&
      auto_tagging_tracks_sent === 0 &&
      firstPane?.results?.length === 0
    ) {
      return (
        <>
          <FacetedRow
            explorerWidth={explorerWidth}
            firstPaneVisible={this.state?.firstPaneVisible}
          />
          <AutoTagging />
        </>
      )
    }

    return (
      <>
        <div className="Explorer_FacetedRow">
          <FacetedRow
            explorerWidth={explorerWidth}
            firstPaneVisible={this.state?.firstPaneVisible}
          />
        </div>
        <ExplorerDropWrapper library={this.props.app.selectedLibrary}>
          <div
            className={explorerClassName}
            data-testid="explorer"
            style={style}
            ref={this._paneNodeRef}
            onScroll={evt => {
              if (evt.target === this._paneNodeRef.current) {
                let firstPaneVisible =
                  (this._paneNodeRef.current?.scrollLeft ?? 0) > colWidth

                if (firstPaneVisible !== this.state?.firstPaneVisible) {
                  this.setState({
                    firstPaneVisible,
                  })
                }
              }
            }}
          >
            {/* _headerNode */}
            {!customInboxesEnabled && CUSTOM_INBOX === firstPane?.groupType && (
              <div
                className="Explorer_Alert"
                style={{
                  width: colWidth,
                }}
              >
                <Alert>
                  Want more custom inboxes? To add more inboxes or edit existing
                  inboxes,
                  <ContactUsLink> upgrade to Pro.</ContactUsLink>
                </Alert>
              </div>
            )}

            {isAutoTagging && (
              <AutoTagging
                searchResultsCount={firstPane?.results?.length}
                isLoading={firstPane?.loading}
              />
            )}

            <PaneList
              style={innerStyles}
              panes={panes as ExplorerPane[]}
              colCount={colCount}
              colWidth={colWidth}
              library={this.props.app.selectedLibrary}
              user={this.props.user}
              isPhone={this.props.isPhone}
            />
          </div>
        </ExplorerDropWrapper>
      </>
    )
  }
}

// @ts-expect-error type connector
export default connect(mapStateToProps, mapDispatchToProps)(Explorer) as any
